import { IBaseItem, Id, IPaginationParams } from 'communication';

import { TradingContextService } from '../../../../../src/app/services/trading-context.service';
import { IAccount } from './account';
import { IInstrument } from './instrument';

export type OrderAccount = {
  fcmId: string;
  ibId: string;
  id: string;
  name: string;
  connectionId?: string;
};

export interface IOrder extends IBaseItem {
  account: OrderAccount;
  origData?: any;
  isHistorical?: boolean;
  averageFillPrice: number;
  triggerPrice: number;
  description: string;
  quantity: number;
  filledQuantity: number;
  instrument: IInstrument;
  duration: OrderDuration;
  side: OrderSide;
  status: OrderStatus;
  type: OrderType;
  exchange: string;
  symbol: string;
  ocoOrder?: IOrder;
  iceQuantity?: number;
  amount?: number;
  price?: number;
  accountId: Id;
  limitPrice?: number;
  stopPrice?: number;
  timestamp?: number;
  currentSequenceNumber?: string;
  tradingContext?: TradingContextService;
  windowName?: string;
  originatorWindowName?: string;
}

export enum OrderSide {
  Buy = 'Buy',
  Sell = 'Sell',
}

export enum OrderDuration {
  ///  GTD = 'GTD',
  GTC = 'GTC',
  /**
   * Items disabled until the features are fully working.
   * FOK = 'FOK',
   * IOC = 'IOC',
   */
  DAY = 'DAY',
}

export const OrderDurationArray = Object.values(OrderDuration);
Object.freeze(OrderDurationArray);

export enum OrderType {
  Market = 'Market',
  Limit = 'Limit',
  StopMarket = 'StopMarket',
  StopLimit = 'StopLimit',
  // MIT = 'MIT',
  // LIT = 'LIT',
  // ICE = 'ICE'
}

export enum OrderStatus {
  Pending = 'Pending',
  New = 'New',
  PartialFilled = 'PartialFilled',
  Filled = 'Filled',
  Canceled = 'Canceled',
  Rejected = 'Rejected',
  Stopped = 'Stopped',
}

export interface IOrderParams extends IPaginationParams {
  accounts: IAccount[];
  status: OrderStatus;
}

const forbiddenOrders = [
  OrderStatus.Rejected,
  OrderStatus.Filled,
  OrderStatus.Canceled,
];

export function isForbiddenOrder(order) {
  return forbiddenOrders.includes(order.status);
}

export function getPrice(order: IOrder) {
  if (order.type === OrderType.StopMarket || order.type === OrderType.StopLimit)
    return isNaN(order.stopPrice) ? order.triggerPrice : order.stopPrice;

  if (
    order.status === OrderStatus.Filled ||
    order.status === OrderStatus.Pending
  ) {
    return order.averageFillPrice || order.price;
  }

  return isNaN(order.price) ? order.triggerPrice : order.price;
}

export interface OrderPriceSpecs {
  limitPrice?: number;
  stopPrice?: number;
  price?: number;
}

export function getPriceSpecs(
  item: IOrder & { amount: number },
  price: number,
  tickSize: number,
): OrderPriceSpecs | any {
  // @todo Fix downstream types
  const priceSpecs: OrderPriceSpecs = {};
  const multiplier = 1 / tickSize;

  // Round to the nearest tick size
  price = Math.round(price * multiplier) / multiplier;

  if ([OrderType.Limit, OrderType.StopLimit].includes(item.type)) {
    priceSpecs.limitPrice = price;
  }
  if ([OrderType.StopMarket, OrderType.StopLimit].includes(item.type)) {
    priceSpecs.stopPrice = price;
  }
  if (item.type === OrderType.StopLimit) {
    const offset = tickSize * item.amount;
    priceSpecs.limitPrice =
      price + (item.side === OrderSide.Sell ? -offset : offset);
  }

  priceSpecs.price = price;

  return priceSpecs;
}

export function getPriceScecsForDuplicate(item: IOrder) {
  const prices: any = {};
  if ([OrderType.Limit, OrderType.StopLimit].includes(item.type)) {
    prices.limitPrice = +item.price;
  }
  if ([OrderType.StopLimit, OrderType.StopMarket].includes(item.type)) {
    prices.stopPrice = +item.triggerPrice;
  }
  return prices;
}
